import React, { useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { usePageTitle } from '../../hooks/usePageTitle';
import toast from 'react-hot-toast';
import {
  useAdminCategories, useCreateCategory, useUpdateCategory, useDeleteCategory, useUploadCategoryImage,
} from '../../hooks/useMovie';
import { useAlert } from '../../components/ui/AlertModal';
import { getApiErrorMessage } from '../../api/errors';
import { Button } from '../../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter,
} from '../../components/ui/dialog';

const inputClass =
  'w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 outline-none transition-colors focus:border-primary/50 focus:bg-white/[0.05]';

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-medium text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}

function EmptyState({ icon, children }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-xl py-16 text-center ring-1 ring-foreground/[0.06]">
      <Icon icon={icon} className="size-6 text-muted-foreground/40" />
      <p className="text-sm text-muted-foreground">{children}</p>
    </div>
  );
}

export default function AdminCategoriesPage() {
  usePageTitle('Manage Categories · Kast');
  const { data: categories = [], isLoading } = useAdminCategories();
  const createMutation = useCreateCategory();
  const updateMutation = useUpdateCategory();
  const deleteMutation = useDeleteCategory();
  const uploadImageMutation = useUploadCategoryImage();
  const { confirm } = useAlert();

  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const fileInputRefs = useRef({});

  const filteredCategories = categories.filter((cat) =>
    cat.Name.toLowerCase().includes(search.trim().toLowerCase())
  );

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      await createMutation.mutateAsync(newName.trim());
      toast.success('Category created.');
      setNewName('');
      setCreateOpen(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to create category.'));
    }
  };

  const startEdit = (cat) => { setEditingId(cat.Id); setEditName(cat.Name); };

  const saveEdit = async (id) => {
    try {
      await updateMutation.mutateAsync({ id, name: editName.trim() });
      toast.success('Category updated.');
      setEditingId(null);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to update category.'));
    }
  };

  const handleDelete = async (cat) => {
    const ok = await confirm(
      `Delete "${cat.Name}"? Movies in this category will become uncategorized.`,
      { title: 'Delete Category', variant: 'error', isConfirm: true, confirmLabel: 'Delete' }
    );
    if (!ok) return;
    try {
      await deleteMutation.mutateAsync(cat.Id);
      toast.success('Category deleted.');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to delete category.'));
    }
  };

  const handleImagePick = (catId, file) => {
    if (!file) return;
    uploadImageMutation.mutate({ id: catId, file }, {
      onSuccess: () => toast.success('Category image updated.'),
      onError: (err) => toast.error(getApiErrorMessage(err, 'Failed to upload image.')),
    });
  };

  return (
    <div className="px-4 py-6 sm:px-6 sm:py-8 lg:px-10">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-white/5 text-primary ring-1 ring-white/10">
            <Icon icon="ph:folder-bold" className="size-5" />
          </span>
          <div>
            <h1 className="text-lg font-semibold text-foreground">Categories</h1>
            <p className="text-xs text-muted-foreground">Manage categories and the cover image shown on the "Browse by Category" grid</p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {categories.length > 0 && (
            <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary ring-1 ring-primary/25">
              {categories.length} categor{categories.length === 1 ? 'y' : 'ies'}
            </span>
          )}
          <Button size="sm" onClick={() => setCreateOpen(true)} className="gap-1.5">
            <Icon icon="ph:plus-bold" />
            Add Category
          </Button>
        </div>
      </div>

      <div className="relative mb-5 max-w-sm">
        <Icon icon="ph:magnifying-glass-bold" className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground/60" />
        <input
          className={`${inputClass} pl-9`}
          placeholder="Search categories…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {isLoading ? (
        <EmptyState icon="ph:spinner-gap-bold">Loading…</EmptyState>
      ) : categories.length === 0 ? (
        <EmptyState icon="ph:folder-bold">No categories yet</EmptyState>
      ) : filteredCategories.length === 0 ? (
        <EmptyState icon="ph:magnifying-glass-bold">No categories match "{search}"</EmptyState>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {filteredCategories.map((cat) => (
            <div
              key={cat.Id}
              className="flex flex-col gap-3 rounded-xl bg-white/[0.03] p-3 ring-1 ring-foreground/[0.06] transition-colors hover:bg-white/[0.05]"
            >
              <button
                type="button"
                onClick={() => fileInputRefs.current[cat.Id]?.click()}
                title="Click to change image"
                className="relative flex aspect-video w-full shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white/5 text-muted-foreground/40 transition-colors hover:bg-white/10"
              >
                {cat.ImageUrl ? (
                  <img src={cat.ImageUrl} alt={cat.Name} className="h-full w-full object-cover" />
                ) : (
                  <Icon icon="ph:image-bold" className="size-6" />
                )}
                <input
                  ref={(el) => (fileInputRefs.current[cat.Id] = el)}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleImagePick(cat.Id, e.target.files?.[0])}
                />
              </button>

              <div className="min-w-0 text-center">
                {editingId === cat.Id ? (
                  <input
                    className={`${inputClass} text-center`}
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    autoFocus
                  />
                ) : (
                  <h3 className="truncate text-sm font-semibold text-foreground">{cat.Name}</h3>
                )}
                <p className="mt-1 text-xs text-muted-foreground">
                  {cat.MovieCount ?? 0} title{cat.MovieCount === 1 ? '' : 's'}
                </p>
              </div>

              <div className="flex w-full items-center gap-2">
                {editingId === cat.Id ? (
                  <>
                    <Button size="sm" onClick={() => saveEdit(cat.Id)} disabled={updateMutation.isPending} className="flex-1 gap-1.5">
                      <Icon icon="ph:check-bold" />
                      Save
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => setEditingId(null)} aria-label="Cancel" className="shrink-0 gap-1.5">
                      <Icon icon="ph:x-bold" />
                    </Button>
                  </>
                ) : (
                  <>
                    <Button size="sm" variant="secondary" onClick={() => startEdit(cat)} className="flex-1 gap-1.5">
                      <Icon icon="ph:pencil-simple-bold" />
                      Rename
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleDelete(cat)}
                      disabled={deleteMutation.isPending}
                      aria-label={`Delete ${cat.Name}`}
                      className="shrink-0 gap-1.5 bg-destructive/10 text-destructive ring-1 ring-destructive/25 hover:bg-destructive/20"
                    >
                      <Icon icon="ph:trash-bold" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={(next) => { setCreateOpen(next); if (!next) setNewName(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Icon icon="ph:plus-bold" />
              Add Category
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleCreate} className="contents">
            <DialogBody>
              <Field label="Name">
                <input
                  className={inputClass}
                  placeholder="e.g. Thriller"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  autoFocus
                />
              </Field>
            </DialogBody>

            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => setCreateOpen(false)} className="gap-1.5">
                <Icon icon="ph:x-bold" />
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending || !newName.trim()} className="gap-1.5">
                <Icon icon="ph:check-bold" />
                {createMutation.isPending ? 'Adding…' : 'Add Category'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
